/**
 * The save indicator.
 *
 * It says what actually happened and nothing more. "Saved on this iPad" appears
 * only after the transaction commits; a failure says so and stays visible rather
 * than fading away as if nothing went wrong.
 */

import type { SaveStatus as Status } from '../storage/autosave';

export interface SaveStatusProps {
  status: Status;
  /** True when storage is memory-only — private browsing, or a blocked profile. */
  ephemeral: boolean;
}

export function SaveStatus({ status, ephemeral }: SaveStatusProps) {
  const { text, tone } = describe(status, ephemeral);
  return (
    <span className={`save-status is-${tone}`} role="status" aria-live="polite">
      {text}
    </span>
  );
}

export function describe(
  status: Status,
  ephemeral: boolean,
): { text: string; tone: 'quiet' | 'working' | 'good' | 'bad' } {
  if (ephemeral) {
    // Do not offer the comfort of "saved" when this browser will not keep it.
    return { text: 'This session only — storage is unavailable', tone: 'bad' };
  }
  switch (status) {
    case 'idle':
      return { text: 'Saved on this iPad', tone: 'quiet' };
    case 'pending':
    case 'saving':
      return { text: 'Saving…', tone: 'working' };
    case 'saved':
      return { text: 'Saved on this iPad', tone: 'good' };
    case 'error':
      return { text: "Couldn't save — your writing is still on screen", tone: 'bad' };
  }
}
